import React, { useState, useEffect } from "react";
import AgoraRTC, {
    AgoraRTCProvider,
    LocalUser,
    RemoteUser,
    useJoin,
    useLocalMicrophoneTrack,
    useLocalCameraTrack,
    usePublish,
    useRemoteUsers,
} from "agora-rtc-react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { Mic, MicOff, Videocam, VideocamOff, CallEnd } from "@mui/icons-material";
import { SwapHoriz } from "@mui/icons-material";

// Enhanced client configuration with advanced audio processing
const client = AgoraRTC.createClient({
    mode: "rtc",
    codec: "vp8",
    audioProcessing: {
        aec: {
            enabled: false,
            aggressiveness: 2 // More aggressive echo cancellation
        },
        ans: {
            enabled: true,
            aggressiveness: 2 // More aggressive noise suppression
        },
        agc: {
            enabled: true,
            level: 0.8 // Maintain consistent audio level
        }
    }
});


const VideoCallApp = ({ callPayload, callType, caller_id, handleClickClose, callAcceptData, callId }) => {
    const appId = "a5cf241d9cf2453ba9fbfde7ceedc233";
    const channel = callPayload?.call_channel || callAcceptData?.call?.channel_name;
    const token = callPayload?.caller_token || callAcceptData?.caller_token;
    const isVideoCall = callType === "video";
    const uid = caller_id;
    const [calling, setCalling] = useState(false);
    const [micOn, setMic] = useState(true);
    const [cameraOn, setCamera] = useState(true);
    const [showCallModal, setShowCallModal] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [isClientReady, setIsClientReady] = useState(false);

    useEffect(() => {
        if (callAcceptData?.type === "accept_call" && channel && token && isVideoCall && uid && appId) {
            setCalling(true);
            setShowCallModal(true);
        }
        else if (callPayload?.type === "missed_call") {
            handleClickClose(callId);
            setCalling(false);
            setShowCallModal(false);
            setCallDuration(0);
        }
    }, [callPayload, callAcceptData, channel, token, isVideoCall, uid, appId]);

    return (
        <AgoraRTCProvider client={client}>
            <VideoCall
                calling={calling}
                setCalling={setCalling}
                micOn={micOn}
                setMic={setMic}
                cameraOn={cameraOn}
                setCamera={setCamera}
                appId={appId}
                channel={channel}
                token={token}
                isVideoCall={isVideoCall}
                uid={uid}
                showModal={showCallModal}
                closeModal={() => setShowCallModal(false)}
                handleClickClose={handleClickClose}
                callId={callId}
                callDuration={callDuration}
                setCallDuration={setCallDuration}
                setIsClientReady={setIsClientReady}
                isClientReady={isClientReady}
            />
        </AgoraRTCProvider>
    );
};

const VideoCall = ({
    calling,
    setCalling,
    micOn,
    setMic,
    cameraOn,
    setCamera,
    appId,
    channel,
    token,
    isVideoCall,
    uid,
    showModal,
    closeModal,
    handleClickClose,
    callId,
    callDuration,
    setCallDuration,
    setIsClientReady,
    isClientReady,
}) => {
    useEffect(() => {
        if (calling && appId && channel && token && uid) {
            setIsClientReady(true);
        } else {
            setIsClientReady(false);
        }
    }, [calling, appId, channel, token, uid]);

    useJoin(
        { appid: appId, channel, token, uid },
        calling && isClientReady && channel && token && uid && appId
    );

    const useDefaultMicrophone = () => {
        const [defaultMic, setDefaultMic] = useState(null);
    
        useEffect(() => {
            const fetchDefaultMicrophone = async () => {
                try {
                    const devices = await AgoraRTC.getDevices();
                    const microphones = devices.filter(device => device.kind === "audioinput");
                    const defaultMicrophone = microphones.find(device => device.deviceId === "default") || microphones[0];
    
                    if (defaultMicrophone) {
                        setDefaultMic(defaultMicrophone.deviceId);
                    }
                } catch (error) {
                    console.error("Error fetching microphone devices:", error);
                }
            };
    
            fetchDefaultMicrophone();
        }, []);
    
        return defaultMic;
    };
    const defaultMic = useDefaultMicrophone();

    
    const [isLocalUserBig, setIsLocalUserBig] = useState(false);
    // const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
    const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn, {
        AEC: true, // Echo cancellation
        AGC: true, // Automatic Gain Control
        ANS: true, // Noise suppression
        // playbackDeviceId: undefined, // No playback for local mic
        microphoneId: defaultMic,
    });
    
    const { localCameraTrack } = useLocalCameraTrack(cameraOn && isVideoCall);

    usePublish([localMicrophoneTrack, localCameraTrack]);

    const remoteUsers = useRemoteUsers();
    useEffect(() => {
        let timer;
        if (calling) {
            timer = setInterval(() => {
                setCallDuration((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(timer);
        }
        return () => clearInterval(timer);
    }, [calling]);

    const handleEndCall = () => {
        handleClickClose(callId);
        closeModal();
        setCalling(false);
        setCallDuration(0);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };

    useEffect(() => {
        const handleRemoteUserLeft = (user) => {
            console.log("Remote user left:", user);
            handleEndCall();
        };

        client.on("user-left", handleRemoteUserLeft);

        return () => {
            client.off("user-left", handleRemoteUserLeft);
        };
    }, []);

    useEffect(() => {
        const handleUserPublished = async (user, mediaType) => {
            await client.subscribe(user, mediaType);
            if (mediaType === 'video') {
                console.log("Remote video track received:", user.videoTrack);
                user.videoTrack.play();
            }
            if (mediaType === 'audio') {
                user.audioTrack.setVolume(100); // Maximum volume // Set appropriate volume
                user.audioTrack.play(); // Play remote audio
            }
        };

        const handleUserUnpublished = (user, mediaType) => {
            console.log("Remote user unpublished:", user, mediaType);
            if (mediaType === 'video' && user.videoTrack) {
                user.videoTrack.stop();
            }
        };

        client.on("user-published", handleUserPublished);
        client.on("user-unpublished", handleUserUnpublished);

        return () => {
            client.off("user-published", handleUserPublished);
            client.off("user-unpublished", handleUserUnpublished);
        };
    }, []);

    useEffect(() => {
        const handleMicrophoneChange = (info) => {
            console.log("Microphone changed:", info);
        };
    
        AgoraRTC.onMicrophoneChanged = handleMicrophoneChange;
    
        return () => {
            AgoraRTC.onMicrophoneChanged = null; // Cleanup listener
        };
    }, []);
    

    return (
        <Modal open={showModal}>
            <Box sx={{
                width: { xs: '90%', sm: '60%', md: '70%', lg: '50%', },
                height: { xs: '80vh', sm: '88vh', md: '80vh', lg: '85vh', },
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 2,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                overflow: "hidden",
            }}>
                <div style={{
                    position: "relative",
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    borderRadius: "12px",
                }}>
                    {isLocalUserBig ? (
                        <LocalUser
                            audioTrack={localMicrophoneTrack}
                            videoTrack={localCameraTrack}
                            cameraOn={cameraOn}
                            micOn={micOn}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        remoteUsers.length > 0 && (
                            <RemoteUser
                                user={remoteUsers[0]}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        )
                    )}

                    <Box style={{
                        position: "absolute",
                        top: "1rem",
                        right: "1rem",
                        maxWidth: "150px",
                        aspectRatio: "1",
                        borderRadius: "12px",
                        overflow: "hidden",
                        backgroundColor: "#000",
                    }} sx={{
                        width: { xs: "30%", md: "20%", lg: "20%" },
                        height: { xs: "25%" },
                    }}>
                        <IconButton
                            style={{
                                position: "absolute",
                                top: "1rem",
                                left: "1rem",
                                zIndex: 10,
                                backgroundColor: "#ffffffaa",
                                borderRadius: "50%",
                                cursor: "pointer",
                                height: '30px',
                                width: '30px',
                            }}
                            onClick={() => setIsLocalUserBig((prev) => !prev)}
                        >
                            <SwapHoriz />
                        </IconButton>
                        {!isLocalUserBig ? (
                            <LocalUser
                                audioTrack={localMicrophoneTrack}
                                videoTrack={localCameraTrack}
                                cameraOn={cameraOn}
                                micOn={micOn}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            remoteUsers.length > 0 && (
                                <RemoteUser
                                    user={remoteUsers[0]}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            )
                        )}
                    </Box>
                </div>
                <div style={{
                    textAlign: "center",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    padding: "0.5rem",
                }}>
                    {formatTime(callDuration)}
                </div>
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "1rem",
                    padding: "0.5rem 0",
                    flexWrap: "wrap",
                }}>
                    <IconButton
                        color={micOn ? "primary" : "error"}
                        onClick={() => setMic((prev) => !prev)}
                    >
                        {micOn ? <Mic /> : <MicOff />}
                    </IconButton>
                    <IconButton
                        color={cameraOn ? "primary" : "error"}
                        onClick={() => setCamera((prev) => !prev)}
                    >
                        {cameraOn ? <Videocam /> : <VideocamOff />}
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={handleEndCall}
                        sx={{
                            backgroundColor: "#ff4d4d",
                            color: "#fff",
                            "&:hover": {
                                backgroundColor: "#e60000",
                                transform: "scale(1.1)",
                            },
                        }}
                    >
                        <CallEnd />
                    </IconButton>
                </div>
            </Box>
        </Modal>
    );
};

export default VideoCallApp;